import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { EyeOff, Eye, Trash2, ExternalLink, Globe, Lock } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import AdminLayout from '@/components/AdminLayout';
import AdminConfirm from '@/components/AdminConfirm';
import SafeImg from '@/components/SafeImg';
import { FilterBar, SelectPill, Pagination, StateMsg } from '@/components/AdminFilters';

const CATEGORY_OPTS = [
  { value: 'all', label: '모든 카테고리' }, { value: 'fashion', label: '패션' },
  { value: 'beauty', label: '뷰티' }, { value: 'food', label: '식품' }, { value: 'pet', label: '펫' },
  { value: 'kids', label: '키즈' }, { value: 'living', label: '리빙' }, { value: 'others', label: '기타' },
];
const STATUS_OPTS = [
  { value: 'all', label: '모든 상태' }, { value: '판매 중', label: '판매 중' },
  { value: '품절', label: '품절' }, { value: '판매 종료', label: '판매 종료' },
];
const VIS_OPTS = [
  { value: 'all', label: '모든 공개상태' }, { value: 'public', label: '공개' },
  { value: 'private', label: '비공개' }, { value: 'hidden', label: '숨김' },
];

const AdminProducts = ({ user }) => {
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get('q') || '');
  const [seller, setSeller] = useState(params.get('seller') || '');
  const [category, setCategory] = useState(params.get('category') || 'all');
  const [status, setStatus] = useState(params.get('status') || 'all');
  const [visibility, setVis] = useState(params.get('visibility') || 'all');
  const [page, setPage] = useState(Number(params.get('page') || 1));
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [confirm, setConfirm] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/products', {
        params: {
          q: q || undefined, seller: seller || undefined,
          category: category !== 'all' ? category : undefined,
          status: status !== 'all' ? status : undefined,
          visibility: visibility !== 'all' ? visibility : undefined,
          page, page_size: 20,
        },
      });
      setData(res.data);
    } catch (e) { toast.error(e?.response?.data?.detail || '실패했습니다.'); }
    finally { setLoading(false); }
  }, [q, seller, category, status, visibility, page]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const p = {};
    if (q) p.q = q; if (seller) p.seller = seller;
    if (category !== 'all') p.category = category; if (status !== 'all') p.status = status;
    if (visibility !== 'all') p.visibility = visibility; if (page > 1) p.page = String(page);
    setParams(p, { replace: true });
  }, [q, seller, category, status, visibility, page, setParams]);

  const reset = () => { setQ(''); setSeller(''); setCategory('all'); setStatus('all'); setVis('all'); setPage(1); };
  const run = async (fn) => { try { const r = await fn(); toast.success(r.data?.message || '완료되었습니다.'); load(); } catch (e) { toast.error(e?.response?.data?.detail || '실패했습니다.'); } };

  const publish = (p) => run(() => api.post(`/admin/products/${p.product_id}/publish`));
  const unpublish = (p) => run(() => api.post(`/admin/products/${p.product_id}/unpublish`));
  const hide = (p) => run(() => api.post(`/admin/products/${p.product_id}/hide`));
  const unhide = (p) => run(() => api.post(`/admin/products/${p.product_id}/unhide`));
  const del = (p) => setConfirm({
    title: '상품을 삭제하시겠습니까?',
    message: `"${p.name}" 상품은 소비자 화면에서 즉시 사라집니다.`,
    confirmLabel: '삭제', tone: 'danger',
    onConfirm: () => run(() => api.delete(`/admin/products/${p.product_id}`)),
  });

  const items = data.items || [];

  return (
    <AdminLayout user={user} active="products">
      <div className="mb-4">
        <h1 className="text-2xl sm:text-3xl font-black tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>상품 관리</h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">상품 공개 · 비공개 · 숨김 · 삭제를 관리합니다.</p>
      </div>

      <FilterBar q={q} setQ={setQ} onSearch={() => { setPage(1); load(); }} onReset={reset} placeholder="상품명 검색">
        <input value={seller} onChange={(e) => setSeller(e.target.value)} placeholder="판매자명"
          className="px-3 py-2 border rounded-full bg-white dark:bg-[#141414] border-black/10 dark:border-white/10 text-sm" data-testid="filter-seller" />
        <SelectPill value={category}   onChange={(v) => { setCategory(v); setPage(1); }} options={CATEGORY_OPTS} testid="filter-category" />
        <SelectPill value={status}     onChange={(v) => { setStatus(v); setPage(1); }}   options={STATUS_OPTS}   testid="filter-status" />
        <SelectPill value={visibility} onChange={(v) => { setVis(v); setPage(1); }}      options={VIS_OPTS}      testid="filter-visibility" />
      </FilterBar>

      <div className="bg-white dark:bg-[#141414] border border-black/5 dark:border-white/10 rounded-xl overflow-hidden" data-testid="admin-products-table">
        <StateMsg loading={loading} empty={!loading && items.length === 0} />
        {!loading && items.length > 0 && (
          <>
            <table className="hidden md:table w-full text-sm">
              <thead className="bg-black/5 dark:bg-white/5 text-xs uppercase tracking-wider text-gray-500 text-left">
                <tr>
                  <th className="px-4 py-2">상품</th>
                  <th className="px-4 py-2">판매자</th>
                  <th className="px-4 py-2">가격</th>
                  <th className="px-4 py-2">카테고리 · 상태</th>
                  <th className="px-4 py-2">공개</th>
                  <th className="px-4 py-2 text-right">작업</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-black/5 dark:divide-white/10">
                {items.map((p) => (
                  <tr key={p.product_id} data-testid={`product-row-${p.product_id}`} className="hover:bg-black/5 dark:hover:bg-white/5">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <SafeImg src={p.image} kind="product" alt={p.name} className="w-10 h-10 rounded-md object-cover" />
                        <div>
                          <div className="font-bold truncate max-w-xs">{p.name}</div>
                          <div className="text-[11px] text-gray-500">방송 연결 {p.linked_broadcast_count || 0}개</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs">
                      <Link to={`/seller/${p.seller_id}`} target="_blank" className="inline-flex items-center gap-1 hover:underline">{p.seller_name || '-'} <ExternalLink size={10} /></Link>
                    </td>
                    <td className="px-4 py-3 text-xs">
                      <div>{(p.sale_price || 0).toLocaleString('ko-KR')}원</div>
                      {p.original_price > p.sale_price && <div className="text-[10px] text-gray-400 line-through">{p.original_price?.toLocaleString('ko-KR')}원</div>}
                    </td>
                    <td className="px-4 py-3 text-xs">{p.category} · {p.sale_status}</td>
                    <td className="px-4 py-3 text-xs">
                      {p.admin_hidden ? <span className="text-red-600 font-bold">숨김</span> : (p.is_public === false ? '비공개' : '공개')}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-2 flex-wrap">
                        {p.is_public === false ? (
                          <button onClick={() => publish(p)} className="text-emerald-600 hover:underline text-xs font-bold" data-testid={`product-publish-${p.product_id}`}><Globe size={12} className="inline" /> 공개</button>
                        ) : (
                          <button onClick={() => unpublish(p)} className="text-amber-600 hover:underline text-xs font-bold" data-testid={`product-unpublish-${p.product_id}`}><Lock size={12} className="inline" /> 비공개</button>
                        )}
                        {p.admin_hidden ? (
                          <button onClick={() => unhide(p)} className="text-emerald-600 hover:underline text-xs font-bold" data-testid={`product-unhide-${p.product_id}`}><Eye size={12} className="inline" /> 숨김 해제</button>
                        ) : (
                          <button onClick={() => hide(p)} className="text-amber-600 hover:underline text-xs font-bold" data-testid={`product-hide-${p.product_id}`}><EyeOff size={12} className="inline" /> 숨김</button>
                        )}
                        <button onClick={() => del(p)} className="text-red-600 hover:underline text-xs font-bold" data-testid={`product-delete-${p.product_id}`}><Trash2 size={12} className="inline" /> 삭제</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="md:hidden divide-y divide-black/5 dark:divide-white/10">
              {items.map((p) => (
                <div key={p.product_id} className="px-4 py-3 flex gap-3" data-testid={`product-card-${p.product_id}`}>
                  <SafeImg src={p.image} kind="product" alt={p.name} className="w-14 h-14 rounded-md object-cover" />
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-sm truncate">{p.name}</div>
                    <div className="text-xs text-gray-500 truncate">{p.seller_name}</div>
                    <div className="text-[11px] text-gray-400">{(p.sale_price || 0).toLocaleString('ko-KR')}원 · {p.sale_status} · {p.admin_hidden ? '숨김' : (p.is_public === false ? '비공개' : '공개')}</div>
                    <div className="flex gap-2 mt-1 flex-wrap">
                      {p.is_public === false
                        ? <button onClick={() => publish(p)} className="text-emerald-600 text-xs font-bold">공개</button>
                        : <button onClick={() => unpublish(p)} className="text-amber-600 text-xs font-bold">비공개</button>}
                      {p.admin_hidden
                        ? <button onClick={() => unhide(p)} className="text-emerald-600 text-xs font-bold">숨김 해제</button>
                        : <button onClick={() => hide(p)} className="text-amber-600 text-xs font-bold">숨김</button>}
                      <button onClick={() => del(p)} className="text-red-600 text-xs font-bold ml-auto">삭제</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
      <Pagination page={page} total={data.total} pageSize={20} onChange={setPage} />
      <AdminConfirm state={confirm} onClose={() => setConfirm(null)} />
    </AdminLayout>
  );
};

export default AdminProducts;
